<script setup lang="ts">
import type { Modifier, Placement } from "@popperjs/core";
import { createPopper, detectOverflow } from "@popperjs/core";
import { computed, nextTick, provide, ref, useTemplateRef, watch } from "vue";

import { useNativeEvents } from "@/composables/useNativeEvents";
import { eventTarget } from "@/utils/dom/dom";

import type { Props as BasePopupProps } from "../BasePopup/BasePopup.vue";
import BasePopup from "../BasePopup/BasePopup.vue";
import { dropdownKey } from "./injectKeys";

interface BaseProps {
  offset?: { left?: number; top?: number };
  noFlip?: boolean; // avoid flipping the placement from bottom to top, but instead just move the contents to fit inside the window
  disableTeleport?: boolean;
  width?: BasePopupProps["width"];
  maxHeight?: BasePopupProps["maxHeight"];
  disabled?: boolean; // disables user interactions on the trigger
  readonly?: boolean; // disables user interactions, but without the 'disabled' styling
  placement?: Placement;
}

// TODO this would be the correct props typing, but it confuses the current version of vue-tsc
/*
interface WithExternalOpenState {
  isOpen: boolean; // manage the open state from outside
  stayOpenOnContentClick?: never; // don't close the dropdown when the user clicks on the content container
  stayOpenOnOutsideClick?: never; // don't close the dropdown when the user clicks outside the trigger and content container
}

interface WithInternalOpenState {
  isOpen?: never;
  stayOpenOnContentClick?: boolean;
  stayOpenOnOutsideClick?: boolean;
}

type WithOpenState=WithExternalOpenState|WithInternalOpenState;
*/
interface WithOpenState {
  isOpen?: boolean;
  stayOpenOnContentClick?: boolean;
  stayOpenOnOutsideClick?: boolean;
}

export type DropdownMenuProps = BaseProps & WithOpenState;

const props = withDefaults(defineProps<DropdownMenuProps>(), {
  isOpen: undefined,
  offset: () => ({ left: 0, top: 5 }),
  noFlip: false,
  stayOpenOnContentClick: false,
  stayOpenOnOutsideClick: false,
  disableTeleport: false,
  width: "auto",
  maxHeight: "lg",
  disabled: false,
  placement: "bottom-start",
});
const emit = defineEmits<{ close: []; open: [] }>();

const isOpenInternal = ref(false);
const triggerRef = useTemplateRef("trigger");
const contentRef = useTemplateRef("content");
const popper = ref<ReturnType<typeof createPopper> | null>(null);

const contentId = `dropdown-content_${Math.random().toString().substring(2)}`;

provide(dropdownKey, { close });

defineExpose({ open, close });

const isStateManagedExternally = computed(() => props.isOpen !== undefined);
const computedOpenState = computed(() => props.isOpen ?? isOpenInternal.value);
const { addEventListener, removeEventListeners } = useNativeEvents();
let closed = 0;

function open() {
  // when this is called by e.g. a button click
  // and the same button is clicked again
  // the dropdown is closed because of handleOutsideClick
  // then avoid immediate reopening of the dropdown menu
  if (Date.now() - closed > 250) {
    isOpenInternal.value = true;
  }
}

function close() {
  isOpenInternal.value = false;
}

const move: Modifier<"move", Record<string, never>> = {
  name: "move",
  enabled: true,
  phase: "main",
  requiresIfExists: ["offset"],
  fn({ state }) {
    const overflow = detectOverflow(state, {});
    if (overflow.bottom > 0 && state.modifiersData.popperOffsets) {
      state.modifiersData.popperOffsets.y -= overflow.bottom;
    }
  },
};

const handleKeyEvent = (event: KeyboardEvent) => {
  if (isStateManagedExternally.value || !isOpenInternal.value) return;

  // close the dropdown when the user presses the escape
  // (or tab key, if the event is coming from the dropdown -> on keydown)
  if (event.key === "Escape" || (event.key === "Tab" && event.type === "keydown")) {
    isOpenInternal.value = false;
    event.stopPropagation();
  }
};

/**
 * Focuses the trigger element when the dropdown is closed.
 */
const focusOnTrigger = async () => {
  await nextTick();
  const trigger = triggerRef.value?.querySelector<HTMLElement>("[data-dropdown-trigger]");
  trigger?.focus();
};

// only triggered when the component manges its own open state
const handleOutsideClick = (event: MouseEvent) => {
  const target = eventTarget(event);
  if (
    !triggerRef.value?.contains(target) &&
    !contentRef.value?.contains(target) &&
    !target.closest(".base-popup") //clicked on another popup, assume it's a child popup and don't close
  ) {
    // Give time to the browser (FF, chrome works without timeout) to dispatch possible blur events inside the dropdown
    // needed by e.g. SetStoryPoints.
    setTimeout(() => {
      closed = Date.now();
      isOpenInternal.value = false;
    }, 0);
  }
};

function handleTriggerClick() {
  if (isStateManagedExternally.value) return;
  if (props.disabled || props.readonly) return;

  isOpenInternal.value = !isOpenInternal.value;
}

function handleContentClick() {
  if (isStateManagedExternally.value) return;

  if (!props.stayOpenOnContentClick) {
    isOpenInternal.value = false;
  }
}

watch(isOpenInternal, (val) => {
  if (!val) emit("close");
});

// create a popper when the dropdown is opened
watch(computedOpenState, async (state) => {
  if (!state && popper.value) {
    popper.value.destroy();
    popper.value = null;
    emit("close");
  }

  if (!state) {
    removeEventListeners();
    void focusOnTrigger();
    return;
  }

  await nextTick();

  if (!props.stayOpenOnOutsideClick) {
    addEventListener(document, "pointerdown", handleOutsideClick, true);
  }

  // Handle both local and global key events (to ensure the dropdown is closed when
  // the user presses escape, even if the focus has left the dropdown)
  addEventListener(triggerRef.value, "keydown", handleKeyEvent);
  addEventListener(contentRef.value, "keydown", handleKeyEvent);
  addEventListener(document, "keyup", handleKeyEvent);

  emit("open");

  // TODO typing of createPopper does not accept undefined, so triggerRef.value? is not correct
  // either typing is wrong or don't createPopper when triggerRef is undefined
  createPopper(
    triggerRef.value?.firstElementChild as HTMLElement,
    contentRef.value?.firstElementChild as HTMLElement,
    {
      // to not flicker search dropdowns when zooming, might break in other places?
      strategy: "fixed",
      placement: props.placement ?? "bottom-start",
      modifiers: [
        { name: "offset", options: { offset: [props.offset.left, props.offset.top] } },
        ...(props.noFlip ? [{ name: "flip", enabled: false }, move] : []),
      ],
    },
  );
});
</script>

<template>
  <div class="dropdown-menu" :aria-owns="computedOpenState ? contentId : undefined">
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
    <div
      ref="trigger"
      :class="['trigger', { disabled }]"
      tabindex="-1"
      @click="handleTriggerClick"
      @keydown.space.stop
    >
      <slot name="trigger" :is-open="computedOpenState" :content-id />
    </div>
    <Teleport v-if="computedOpenState" :disabled="disableTeleport" to="body">
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events vuejs-accessibility/no-static-element-interactions-->
      <div :id="contentId" ref="content" data-no-rectangle-select @click.stop="handleContentClick">
        <BasePopup
          v-scrollable-on-board
          v-focus-trap="{ fallbackFocus: triggerRef }"
          class="content"
          :class="$attrs.class"
          :width
          :max-height
          tabindex="-1"
        >
          <!-- ^ tabindex of -1 to make popup focusable -->
          <slot />
        </BasePopup>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/z-index";

.dropdown-menu {
  display: inline-block;

  .trigger {
    height: 100%;
    cursor: pointer;
    position: relative;

    &.disabled {
      cursor: not-allowed;
    }
  }
}

.content {
  z-index: z-index.$popup-menu;

  // TODO: remove this when the list-item component is deleted
  &:deep(.list-item) {
    border-radius: 4px;
  }
}
</style>
